import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, ExternalLink, Check, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import TokenActivityLedger from "../components/TokenActivityLedger";
import BrainPanel from "../components/BrainPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfileSettingsPage = () => {
  const { token } = useAuth();
  const auth = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({
    handle: "", display_name: "", bio: "", avatar_url: "", cover_url: "", links: ["", "", ""],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [handleStatus, setHandleStatus] = useState(null); // null | "checking" | "available" | "taken" | "format" | "reserved"

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/profiles/me`, { headers: auth });
        const p = r.data.profile;
        setProfile(p);
        setForm({
          handle: p.handle || "",
          display_name: p.display_name || "",
          bio: p.bio || "",
          avatar_url: p.avatar_url || "",
          cover_url: p.cover_url || "",
          links: [...(p.links || []), "", "", ""].slice(0, 3),
        });
      } catch (e) {
        toast.error("Could not load profile.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Live handle availability check (debounced)
  useEffect(() => {
    if (!form.handle || form.handle === profile?.handle) {
      setHandleStatus(null);
      return undefined;
    }
    if (form.handle.length < 3) {
      setHandleStatus("format");
      return undefined;
    }
    setHandleStatus("checking");
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/profiles/handles/check?handle=${encodeURIComponent(form.handle)}`, { headers: auth });
        setHandleStatus(r.data.available ? "available" : (r.data.reason || "taken"));
      } catch (e) {
        // 422 = format violation while typing — surface it without a toast
        if (e?.response?.status === 422) {
          setHandleStatus("format");
        } else {
          setHandleStatus(null);
        }
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.handle, profile?.handle, auth]);

  const updateLink = (idx, val) => {
    setForm(f => ({ ...f, links: f.links.map((l, i) => i === idx ? val : l) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        handle: form.handle.trim().toLowerCase() || undefined,
        display_name: form.display_name,
        bio: form.bio,
        avatar_url: form.avatar_url || null,
        cover_url: form.cover_url || null,
        links: form.links.filter(l => l && l.trim()),
      };
      const r = await axios.put(`${API}/profiles/me`, payload, { headers: auth });
      setProfile(r.data.profile);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  const profileUrl = profile?.handle
    ? `${window.location.origin}/creator/${profile.handle}`
    : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6" data-testid="profile-settings-page">
      <div>
        <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-blue-500">
          Settings · Public profile
        </p>
        <h1 className="text-3xl md:text-4xl font-black text-ink-950 tracking-tight">
          How creators see you on XEA.
        </h1>
      </div>

      {profileUrl && (
        <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4 flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-700">
            Your shareable URL
          </span>
          <a
            href={profileUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="profile-public-url"
            className="font-bold text-ink-950 underline truncate"
          >
            {profileUrl}
          </a>
          <a href={profileUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-4 h-4 text-blue-700" />
          </a>
        </div>
      )}

      <div className="bg-white border border-ink-200 rounded-3xl p-5 space-y-4">
        <Field label="Handle">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 font-bold">@</span>
            <input
              type="text"
              value={form.handle}
              onChange={(e) => setForm({ ...form, handle: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 24) })}
              maxLength={24}
              data-testid="profile-handle-input"
              className="w-full pl-7 pr-10 py-2 rounded-xl bg-ink-50 border border-ink-200 focus:outline-none focus:bg-white focus:border-blue-500 text-base font-bold"
              placeholder="your_handle"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {handleStatus === "checking" && <Loader2 className="w-4 h-4 animate-spin text-ink-400" />}
              {handleStatus === "available" && <Check className="w-4 h-4 text-emerald-500" />}
              {(handleStatus === "taken" || handleStatus === "reserved" || handleStatus === "format") && <X className="w-4 h-4 text-rose-500" />}
            </span>
          </div>
          <p className="text-[10px] text-ink-500 mt-1">
            3-24 chars · letters, numbers, underscore. {handleStatus === "taken" ? "🔴 Taken." : handleStatus === "reserved" ? "🔴 Reserved." : handleStatus === "available" ? "🟢 Available." : ""}
          </p>
        </Field>

        <Field label="Display name">
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value.slice(0, 64) })}
            maxLength={64}
            data-testid="profile-displayname-input"
            className="w-full p-2 rounded-xl bg-ink-50 border border-ink-200 focus:outline-none focus:bg-white focus:border-blue-500"
          />
        </Field>

        <Field label="Bio (240 chars)">
          <textarea
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value.slice(0, 240) })}
            rows={3}
            maxLength={240}
            data-testid="profile-bio-input"
            placeholder="Tell creators what you make, who you make it for…"
            className="w-full p-2 rounded-xl bg-ink-50 border border-ink-200 focus:outline-none focus:bg-white focus:border-blue-500 text-sm"
          />
          <p className="text-[10px] text-ink-400 mt-1">{form.bio.length} / 240</p>
        </Field>

        <Field label="Avatar URL (optional)">
          <input
            type="url"
            value={form.avatar_url}
            onChange={(e) => setForm({ ...form, avatar_url: e.target.value })}
            data-testid="profile-avatar-input"
            placeholder="https://…"
            className="w-full p-2 rounded-xl bg-ink-50 border border-ink-200 focus:outline-none focus:bg-white focus:border-blue-500 text-sm"
          />
        </Field>

        <Field label="Cover image URL (optional)">
          <input
            type="url"
            value={form.cover_url}
            onChange={(e) => setForm({ ...form, cover_url: e.target.value })}
            data-testid="profile-cover-input"
            placeholder="https://…"
            className="w-full p-2 rounded-xl bg-ink-50 border border-ink-200 focus:outline-none focus:bg-white focus:border-blue-500 text-sm"
          />
        </Field>

        <Field label="Links (up to 3)">
          {form.links.slice(0, 3).map((l, i) => (
            <input
              key={i}
              type="url"
              value={l}
              onChange={(e) => updateLink(i, e.target.value)}
              data-testid={`profile-link-${i}`}
              placeholder={i === 0 ? "https://your-site.com" : i === 1 ? "https://twitter.com/you" : "https://instagram.com/you"}
              className="w-full p-2 rounded-xl bg-ink-50 border border-ink-200 focus:outline-none focus:bg-white focus:border-blue-500 text-sm mb-2"
            />
          ))}
        </Field>

        <div className="flex justify-end pt-2">
          <Button
            type="button"
            onClick={save}
            disabled={saving || handleStatus === "taken" || handleStatus === "reserved" || handleStatus === "format"}
            data-testid="profile-save"
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save profile
          </Button>
        </div>
      </div>

      <TokenActivityLedger />

      <BrainPanel />
    </div>
  );
};


const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500 mb-1">
      {label}
    </span>
    {children}
  </label>
);


export default ProfileSettingsPage;
